import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import PowerSelect from 'ember-power-select/components/power-select';
import { not } from 'ember-truth-helpers';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import UiButton from 'garaje/components/ui-button';
import doubleConfirmation from 'garaje/helpers/double-confirmation';
import { type BusinessHoursDayDraft, type HourOption } from 'garaje/utils/presence/business-hours';
import type BusinessHoursManager from 'garaje/utils/presence/business-hours-manager';

// Per-location business hours, rendered inside the Location settings page's "Workplace" section. A
// collapsed SettingsPanel by default — "Edit" when a config exists, "Enable" when it doesn't (Enable
// replaces the master toggle: it expands the panel and turns business hours on, unsaved until Save).
// The weekday selector + start/end dropdowns mirror the Desk "Booking hours" panel. State +
// persistence live on the shared BusinessHoursManager.
interface LocationSettingsBusinessHoursSignature {
  Args: {
    manager: BusinessHoursManager;
  };
  Element: HTMLElement;
}

export default class LocationSettingsBusinessHours extends Component<LocationSettingsBusinessHoursSignature> {
  @action
  open(): void {
    this.args.manager.businessHoursPanelOpen = true;
  }

  @action
  close(): void {
    this.args.manager.businessHoursPanelOpen = false;
  }

  // Enable replaces the master toggle: expand the panel and turn business hours on (unsaved until
  // Save). Shared with the report notice's "Set now" link via the manager.
  @action
  enable(): void {
    this.args.manager.startConfiguringBusinessHours();
  }

  @action
  rollback(): void {
    this.args.manager.rollbackBusinessHours();
  }

  @action
  async save(): Promise<void> {
    const saved = await this.args.manager.saveBusinessHours();
    if (saved) this.close();
  }

  // Turn business hours off and persist immediately (mirrors the page's other deactivate controls).
  @action
  async disable(): Promise<void> {
    this.args.manager.toggleBusinessHours(false);
    const saved = await this.args.manager.saveBusinessHours();
    if (saved) this.close();
  }

  // Weekday selector button: toggle that day open/closed (newly opened days default to 12am–11:59pm).
  @action
  toggleDay(day: BusinessHoursDayDraft): void {
    this.args.manager.toggleBusinessHoursDay(day, !day.enabled);
  }

  @action
  setDayStart(day: BusinessHoursDayDraft, option: HourOption): void {
    this.args.manager.setBusinessHoursDayStart(day, option.value);
  }

  @action
  setDayEnd(day: BusinessHoursDayDraft, option: HourOption): void {
    this.args.manager.setBusinessHoursDayEnd(day, option.value);
  }

  <template>
    {{! id="business-hours" is the scroll target for the PCC "Configure business hours" deep links
        (see the account-settings controller's configureBusinessHours + the index route's scrollTo). }}
    <SettingsPanel
      id="business-hours"
      data-test-location-business-hours-panel
      @isOpen={{@manager.businessHoursPanelOpen}}
      as |panel|
    >
      <panel.header
        @iconUri="/assets/images/features/connect.svg"
        @title="Business hours"
        @onCancel={{doubleConfirmation close=this.close rollback=this.rollback dirty=@manager.configDirty}}
        as |SPHeader|
      >
        <SPHeader.description>
          Set this location's business hours.
          <a href="https://envoy.help/en/articles/12159459-presence-control-center" target="_blank" rel="noopener">
            Learn more
          </a>
        </SPHeader.description>
        <SPHeader.controls>
          {{#if @manager.businessHoursPanelOpen}}
            {{#if @manager.businessHoursConfigured}}
              <UiButton {{on "click" this.disable}} class="btn btn--small btn--outlineFill" data-test-disable-button>
                Disable
              </UiButton>
            {{/if}}
            <UiButton
              {{on "click" this.save}}
              class="btn btn--small btn--brand"
              disabled={{@manager.businessHoursSaveDisabled}}
              data-test-save-button
            >
              Save
            </UiButton>
          {{else if @manager.businessHoursConfigured}}
            <UiButton {{on "click" this.open}} class="btn btn--small btn--brand" data-test-edit-button>
              Edit
            </UiButton>
          {{else}}
            <UiButton {{on "click" this.enable}} class="btn btn--small btn--brand" data-test-enable-button>
              Enable
            </UiButton>
          {{/if}}
        </SPHeader.controls>
      </panel.header>
      <panel.body as |SPContent|>
        <SPContent class="mb-4">
          {{! Weekday selector — one circular button per day (Sun–Sat); click toggles the day open. }}
          <div class="flex mt-4 mb-5" data-test-business-hours-days>
            {{#each @manager.businessHoursDays as |day|}}
              <button
                type="button"
                class="{{if day.enabled 'text-white bg-gem-70' 'text-carbon-30 bg-carbon-10'}}
                  rounded-full w-8 h-8 mr-2 flex justify-center items-center cursor-pointer"
                aria-label={{day.label}}
                aria-pressed={{if day.enabled "true" "false"}}
                {{on "click" (fn this.toggleDay day)}}
                data-test-business-hours-day-toggle={{day.key}}
              >
                {{day.initial}}
              </button>
            {{/each}}
          </div>

          {{! Column labels over the start/end dropdowns. The spacers mirror each row's day-name column
              and the invisible separator keeps "End time" aligned over the end dropdown. }}
          <div class="flex items-center mb-2 text-sm text-carbon-50" data-test-business-hours-time-labels>
            <div class="w-28 mr-6"></div>
            <div class="w-[104px]">Start time</div>
            <span class="mx-3 invisible" aria-hidden="true">&ndash;</span>
            <div class="w-[104px]">End time</div>
          </div>

          {{! One row per weekday. Options are 15-min slots, filtered so start < end. Closed days show
              disabled dropdowns with a "Closed" placeholder. }}
          {{#each @manager.businessHoursDays as |day|}}
            <div class="flex items-center mb-3" data-test-business-hours-day={{day.key}}>
              <div class="w-28 mr-6">{{day.label}}</div>
              <PowerSelect
                @options={{day.startOptions}}
                @selected={{if day.enabled day.selectedStartOption}}
                @onChange={{fn this.setDayStart day}}
                @disabled={{not day.enabled}}
                @placeholder={{if day.enabled "Select time" "Closed"}}
                @triggerClass="!mb-0 w-[104px]"
                data-test-business-hours-day-start
                as |option|
              >
                {{option.label}}
              </PowerSelect>
              <span class="mx-3 text-sm text-gray-600" aria-hidden="true">&ndash;</span>
              <PowerSelect
                @options={{day.endOptions}}
                @selected={{if day.enabled day.selectedEndOption}}
                @onChange={{fn this.setDayEnd day}}
                @disabled={{not day.enabled}}
                @placeholder={{if day.enabled "Select time" "Closed"}}
                @triggerClass="!mb-0 w-[104px]"
                data-test-business-hours-day-end
                as |option|
              >
                {{option.label}}
              </PowerSelect>
            </div>
          {{/each}}
        </SPContent>
      </panel.body>
    </SettingsPanel>
  </template>
}
